import { useEffect, useRef, useState } from 'react';
import PalmerNavbar from '@/pages/home/components/PalmerNavbar';
import PalmerSectionLabel from '@/pages/home/components/PalmerSectionLabel';
import PalmerFAQ from '@/pages/home/components/PalmerFAQ';
import PalmerFooter from '@/pages/home/components/PalmerFooter';

const projects = [
  {
    id: '01',
    name: 'Sonder Goods',
    bgImage: 'https://framerusercontent.com/images/wA52DtSvQDx894hqLZv4ezfKfz8.png',
    innerImage: 'https://framerusercontent.com/images/WSIwyrpSzX4O0fiESBwPTjSWBE.png',
  },
  {
    id: '02',
    name: 'Halo Wear',
    bgImage: 'https://framerusercontent.com/images/IhwR33YbJAKylGnbmoCW4maBHI.png',
    innerImage: 'https://framerusercontent.com/images/tkYEeCoj1udozbnzQynoaYqCI.png',
  },
  {
    id: '03',
    name: 'Lucent Lab',
    bgImage: 'https://framerusercontent.com/images/G891sPJdh93gPfGSBboEt88Now.png',
    innerImage: 'https://framerusercontent.com/images/YIi7jRxIe8p6gLtM1ZMNpJyVYs.jpeg',
  },
  {
    id: '04',
    name: 'Arc & Bloom',
    bgImage: 'https://framerusercontent.com/images/kSBqNFitJQuBzXuk7tl1FqlAHhs.png',
    innerImage: 'https://framerusercontent.com/images/Jt7zqgTjQMYT15YvEkLGKiF9Cw.png',
  },
  {
    id: '05',
    name: 'Atelier Nara',
    bgImage: 'https://framerusercontent.com/images/svmMd86RbsKfib7KzvpKAUsHrk.png',
    innerImage: 'https://framerusercontent.com/images/7WVAcnCw5jrTdcET3CmMrpU7gf0.png',
  },
];

function WorkCard({ project, index }: { project: typeof projects[0]; index: number }) {
  const [hovered, setHovered] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (cardRef.current) obs.observe(cardRef.current);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={cardRef}
      className="border-b border-white/10 transition-all duration-700"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(30px)',
        transitionDelay: `${index * 80}ms`,
      }}
    >
      <div
        className="relative overflow-hidden cursor-pointer"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{ height: 'clamp(220px, 28vw, 380px)' }}
      >
        {/* Background image */}
        <img
          src={project.bgImage}
          alt={project.name}
          className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700"
          style={{ transform: hovered ? 'scale(1.04)' : 'scale(1)' }}
        />

        {/* Inner floating image - appears on hover */}
        <div
          className="absolute inset-0 flex items-center justify-center transition-all duration-500"
          style={{ opacity: hovered ? 1 : 0 }}
        >
          <div
            className="overflow-hidden"
            style={{
              width: 'clamp(100px, 14vw, 180px)',
              height: 'clamp(100px, 14vw, 180px)',
              transform: hovered ? 'scale(1) translateY(0)' : 'scale(0.85) translateY(20px)',
              transition: 'transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            }}
          >
            <img
              src={project.innerImage}
              alt={project.name}
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>

        {/* Bottom bar with name + number */}
        <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between px-4 py-3 bg-gradient-to-t from-black/60 to-transparent">
          <div className="overflow-hidden">
            <p
              className="text-white text-sm font-medium transition-all duration-300"
              style={{
                fontFamily: "'DM Sans', sans-serif",
                transform: hovered ? 'translateY(-100%)' : 'translateY(0)',
                opacity: hovered ? 0 : 1,
              }}
            >
              {project.name}
            </p>
            <p
              className="text-white text-sm font-medium absolute bottom-3 left-4 transition-all duration-300"
              style={{
                fontFamily: "'DM Sans', sans-serif",
                transform: hovered ? 'translateY(0)' : 'translateY(100%)',
                opacity: hovered ? 1 : 0,
              }}
            >
              {project.name}
            </p>
          </div>
          <span
            className="text-white/60 text-sm"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            ({project.id})
          </span>
        </div>
      </div>
    </div>
  );
}

export default function WorkPage() {
  const headingRef = useRef<HTMLDivElement>(null);
  const [headingVisible, setHeadingVisible] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setHeadingVisible(true); },
      { threshold: 0.1 }
    );
    if (headingRef.current) obs.observe(headingRef.current);
    return () => obs.disconnect();
  }, []);

  return (
    <div className="bg-black min-h-screen" style={{ fontFamily: "'DM Sans', sans-serif" }} id="top">
      <PalmerNavbar />

      {/* Main work section */}
      <section className="pt-24 pb-0">
        {/* Section label */}
        <PalmerSectionLabel left1="© Selected Work プロジェクト" left2="(WDX® — 03)" right="Portfolio" />

        {/* Layout: heading bottom-left, cards right */}
        <div className="relative">
          {/* Cards column - right side, full width on mobile, right 50% on desktop */}
          <div className="lg:ml-[40%]">
            {projects.map((project, i) => (
              <WorkCard key={project.id} project={project} index={i} />
            ))}
          </div>

          {/* Heading - bottom-left, sticky until cards end */}
          <div
            ref={headingRef}
            className="hidden lg:flex flex-col justify-end absolute left-0 bottom-0 pb-8 pl-6"
            style={{ width: '38%', top: 0 }}
          >
            <div
              className="transition-all duration-700"
              style={{
                opacity: headingVisible ? 1 : 0,
                transform: headingVisible ? 'translateY(0)' : 'translateY(30px)',
              }}
            >
              <h1
                className="text-white font-bold leading-none"
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 'clamp(60px, 8vw, 110px)',
                  letterSpacing: '-0.04em',
                  lineHeight: 0.9,
                }}
              >
                All<br />Works
              </h1>
              <sup
                className="text-white font-semibold"
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 'clamp(20px, 2.5vw, 36px)',
                  verticalAlign: 'super',
                  letterSpacing: '-0.02em',
                }}
              >
                (5)
              </sup>
            </div>
          </div>
        </div>

        {/* Mobile heading */}
        <div className="lg:hidden px-6 py-10">
          <h1
            className="text-white font-bold leading-none"
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 'clamp(56px, 14vw, 90px)',
              letterSpacing: '-0.04em',
              lineHeight: 0.9,
            }}
          >
            All<br />Works<sup className="text-3xl">(5)</sup>
          </h1>
        </div>

        {/* Divider line */}
        <div className="h-px bg-white/10 mt-0"></div>
      </section>

      {/* FAQ */}
      <PalmerFAQ />

      {/* Footer */}
      <PalmerFooter />
    </div>
  );
}
